import {Injectable} from "@angular/core";
import {ApiBaseService} from "@portal/api/api-base-service";
import { HttpClient } from "@angular/common/http";
import {Observable} from "rxjs";
import {ApiResponse, ListResponse} from "@portal/api/responses/api-response";
import { Guid } from '@portal/api/guid';
import {CollaboratorAddRequest} from "@portal/api/requests/collaborators/collaborator-add-request";
import {CollaboratorUpdateRequest} from "@portal/api/requests/collaborators/collaborator-update-request";
import {SearchRequest} from "@portal/api/requests/search-request";
import {CollaboratorListResponse} from "@portal/api/responses/collaborators/collaborator-list-response";
import {StorageService} from "@portal/shared/services/storage.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {OrganizationType} from "@portal/api/enums/organization-type";

@Injectable({
    providedIn: 'root'
})
export class CollaboratorsService extends ApiBaseService {

    controller = 'collaborators';

    constructor(
        public http: HttpClient,
        private _storageService: StorageService,
        private _partySelectedService: PartySelectedService,
    ) {
        super(http);
    }
    add(obj: CollaboratorAddRequest): Observable<ApiResponse<Guid>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        const payload: CollaboratorAddRequest & { partyId?: Guid } = {...obj, partyId: (obj as any).partyId ?? partyId};
        if (this.isOrgScope() || !partyId) {
            return this.post<ApiResponse<Guid>, CollaboratorAddRequest & { partyId?: Guid }>(payload, `v1/orgs/${orgId}/collaborators/add`);
        }
        return this.post<ApiResponse<Guid>, CollaboratorAddRequest & { partyId?: Guid }>(payload, `v1/orgs/${orgId}/parties/${partyId}/collaborators/add`);
    }

    update(obj: CollaboratorUpdateRequest): Observable<ApiResponse<Guid>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.put<ApiResponse<Guid>, CollaboratorUpdateRequest>(obj, `v1/orgs/${orgId}/collaborators/update`);
        }
        return this.put<ApiResponse<Guid>, CollaboratorUpdateRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/collaborators/update`);
    }

    getFormEdit(id: Guid): Observable<ApiResponse<CollaboratorUpdateRequest>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<ApiResponse<CollaboratorUpdateRequest>>(`v1/orgs/${orgId}/collaborators/form/edit/${id}`);
        }
        return this.get<ApiResponse<CollaboratorUpdateRequest>>(`v1/orgs/${orgId}/parties/${partyId}/collaborators/form/edit/${id}`);
    }

    listByParty(obj: SearchRequest): Observable<ApiResponse<ListResponse<CollaboratorListResponse[]>>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.post<ApiResponse<ListResponse<CollaboratorListResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/collaborators/listall`);
        }
        return this.post<ApiResponse<ListResponse<CollaboratorListResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/collaborators/listall`);
    }

    deleteCollaborator(id: Guid): Observable<ApiResponse<boolean>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.deleteBase<ApiResponse<boolean>>(`v1/orgs/${orgId}/collaborators/${id}`);
        }
        return this.deleteBase<ApiResponse<boolean>>(`v1/orgs/${orgId}/parties/${partyId}/collaborators/${id}`);
    }

    private getOrgId(): Guid {
        return this._storageService.getOrg()?.id;
    }

    private getPartyId(): Guid {
        return this._partySelectedService.getPartyId() ?? this._storageService.getParty()?.id;
    }

    private isOrgScope(): boolean {
        return this._storageService.getOrg()?.type === OrganizationType.Association;
    }
}
